<script setup lang="ts">
import { ref } from 'vue';
import { ButtonQC, PhoneInputQC } from '@qicgroupdoha/qic-connect-ds';
import { useI18n } from 'vue-i18n';
import CheckIcon from './assets/check.svg';
import CrossIcon from './assets/cross.svg';
import { useUrl, useApi } from '@shared/composables';
import { LOCAL_STORAGE_KEY } from '~/libs/shared/constants/src/common';
import { useValidation, useValidators } from '@qic/validation';
import { trackGtag } from '~/libs/shared/analitycs';

const { locale } = useI18n();
const { assignUrl } = useUrl();
const api = useApi();

interface heroData {
  form_title?: string,
  form_description?: string,
  form_button_text?: string,
  form_button_icon?: any,
  disclaimer?: string,
}

const props = withDefaults(defineProps<{
  content: heroData;
  position: 'scroll' | 'top';
  isSchoolVersion?: boolean;
}>(), {
  isSchoolVersion: false,
});

const phone = ref('');

const isTitle = computed(() => props?.content?.form_title?.trim());
const isSubtitle = computed(() => props?.content?.form_description?.trim());
const isSuccess = ref(false);
const isFail = ref(false);
const isLoading = ref(false);

const {
  getPhoneRules,
  getCommonRules,
} = useValidators();

const phoneRules = getPhoneRules();
const commonRules = getCommonRules();

const validationRules = {
  phone: {
    phoneLength11: phoneRules.phoneMinLengthRebranding,
    phoneStartsWith3567: phoneRules.phoneStartsWith3567,
  },
};

if (props.isSchoolVersion) {
  validationRules.phone.required = commonRules.required;
}

const v$ = useValidation(validationRules, { phone });

const phoneErrors = computed(
  () => v$.value.$errors?.[0]?.$message ? [v$.value.$errors[0].$message as string] : []
);

const onSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$error) return;

  props.isSchoolVersion ? await onSchoolFlow() : onCarFlow();
};

function onCarFlow () {
  const baseFlowPath = `/${locale.value}/car-insurance/flow`;
  localStorage.setItem(LOCAL_STORAGE_KEY.PHONE, clearPhone(phone.value));
  localStorage.setItem(LOCAL_STORAGE_KEY.ENTRY_TYPE, 'auto');
  localStorage.setItem(LOCAL_STORAGE_KEY.ENTRY_POINT, 'main');
  assignUrl(`${baseFlowPath}?redirectURL=${window.location.host}${window.location.pathname}`, {}, true);
}

async function onSchoolFlow () {
  isLoading.value = true;
  isFail.value = false;
  isSuccess.value = false;

  try {
    const res = await api.common.sendSchoolLead({ mobileNo: clearPhone(phone.value) });
    if (res?.status?.value === 'error') throw new Error('Request error');
    isSuccess.value = true;

    trackGtag('click_check_price', {
      product: 'school_fees',
      page: 'marketing_landing',
      section: props.position === 'top' ? 'main_hero_block' : 'secondary_block',
    });
  } catch (e) {
    console.log(e);
    isFail.value = true;
  }

  isLoading.value = false;
}

function clearPhone (phone: string) {
  return phone.replace(/\s/g, '').replace('+', '');
}

function onReset () {
  resetAnalitycs();

  v$.value.$reset();
  isFail.value = false;
  isSuccess.value = false;
  phone.value = '';
}

function resetAnalitycs () {
  // TODO: make analitycs more universal
  if (isFail.value) {
    trackGtag('try_again', {
      product: 'school_fees',
      page: 'marketing_landing',
      section: props.position === 'top' ? 'main_hero_block' : 'secondary_block',
    });
  }
  if (isSuccess.value) {
    trackGtag('click_btn', {
      product: 'school_fees',
      page: 'marketing_landing',
      btn_title: 'great',
      section: props.position === 'top' ? 'main_hero_block' : 'secondary_block',
    });
  }
}

function onPrivacy (e) {
  if (e?.target?.nodeName === 'A') {
    trackGtag('to_privacy_policy', {
      product: 'school_fees',
      page: 'marketing_landing',
      section: props.position === 'top' ? 'main_hero_block' : 'secondary_block',
    });
  }
}
</script>

<template>
  <template v-if="isSuccess">
    <div class="form-result ">
      <div class="form-result__icon">
        <img :src="CheckIcon" :alt="props?.content?.form_success_title">
      </div>
      <div class="form-result__title">
        {{ props?.content?.form_success_title }}
      </div>
      <div class="form-result__description">
        {{ props?.content?.form_success_description }}
      </div>
      <ButtonQC class="form-result__btn" variant="secondary" @click="onReset">
        {{ props?.content?.form_success_button }}
      </ButtonQC>
    </div>
  </template>
  <template v-else-if="isFail">
    <div class="form-result is_fail">
      <div class="form-result__icon">
        <img :src="CrossIcon" :alt="props?.content?.form_fail_title">
      </div>
      <div class="form-result__title">
        {{ props?.content?.form_fail_title }}
      </div>
      <div class="form-result__description">
        {{ props?.content?.form_fail_description }}
      </div>
      <ButtonQC class="form-result__btn" variant="secondary" @click="onReset">
        {{ props?.content?.form_fail_button }}
      </ButtonQC>
    </div>
  </template>
  <template v-else>
    <div class="form-car row-group" :class="['position_' + props?.position, {is_school: isSchoolVersion}]">
      <div v-if="isTitle" class="form-car__title">
        {{ props?.content?.form_title }}
      </div>
      <div v-if="isSubtitle" class="form-car__subtitle">
        {{ props?.content?.form_description }}
      </div>
      <div class="row-list">
        <PhoneInputQC
          v-model="phone"
          country-lock
          class="form-car__input"
          :errors="phoneErrors"
          :didabled="isLoading"
          @input="$emit('input', phone)"
          @keydown.enter="onSubmit"
        />
      </div>
      <div class="row-list">
        <ButtonQC
          size="medium"
          :loading="isLoading"
          :icon-after-content="locale === 'en' ? 'arrow-narrow-right' : 'arrow-narrow-left'"
          @click="onSubmit"
        >
          {{ props?.content?.form_button_text }}
        </ButtonQC>
      </div>
    </div>
    <div class="form-car__privacy" @click="onPrivacy" v-html="props?.content?.disclaimer" />
  </template>
</template>

<style scoped lang="scss">
@import '@/assets/scss/mixins.scss';

.form-result {
  text-align: center;
  margin: auto;

  &.is_fail .form-result__icon {
    background: var(--color-context-fiery-red-fiery-red-50, #FCE9E7);
  }

  &__icon {
    margin: auto;
    width: 84px;
    height: 84px;
    border-radius: 60px;
    background: var(--color-context-gorgeous-green-gorgeous-green-50, #EAF8ED);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 4px;
    img {
      width: 36px;
      height: 36px;
    }
  }

  &__title {
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 28px; /* 116.667% */
    margin-top: 16px;
  }

  &__description {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px; /* 150% */
    margin-top: 4px;
  }

  &__btn {
    width: 100%;
    max-width: 390px;
    margin-top: 24px;
  }
}


.option-flag {
  width: 30px;
  height: 30px;

  &--contain {
    object-fit: contain;
  }
}

.form-car.is_school {
  .form-car__title {
    margin-top: 0;
  }
}

.form-car {
  grid-template-columns: 1fr;
  column-gap: 16px;
  row-gap: 0;
  width: 610px;
  max-width: 100%;
  gap: 16px;

  @include xl {
    width: 550px;
  }

  @include lg {
    width: 100%;
  }

  @include md {
    grid-template-columns: 1fr;
  }


  :deep(.btn) {
    height: 64px;
    font-size: 16px;

    img, svg {
      width: 24px;
      height: 24px;
    }
  }

  &__input {
    width: 100%;
    :deep(.input-container) {
      width: 100%;
      height: 64px;
    }

    @include lg {
      max-width: 100%;
      :deep(.input-container) {
        max-width: 100%;
      }
    }
  }

  .row-list:nth-child(1) {
    flex-basis: 100%;
  }

  .btn {
    white-space: nowrap;
    @include md {
      width: 100%;
      max-width: 100%;
    }

    img {
      margin-inline-start: 10px;
    }
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    line-height: 30px;
    text-align: center;
    padding: 8px 0px;

    @include rtl {
      line-height: 35px;
    }

    @include lg {
      margin-top: 16px;
    }

    @include md {
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      line-height: 22px; /* 122.222% */
      @include rtl {
        line-height: 22px;
      }
    }


  }

  &__subtitle {
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.1px;
    text-align: center;
  }

  &__privacy {
    color: #5E597A;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: center;
    margin-top: 16px;

    @include md {
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 14px; /* 116.667% */
    }

    & ::v-deep a {
      font-family: inherit;
      color: #494BB5;
      &:hover {
        text-decoration: none;
      }
    }
  }
}

.rtl .row-list {
  .btn {
    direction: rtl;
  }
}
</style>
